import React, { useState, useMemo, useCallback } from 'react';
import { orders as ordersApi, priceHistory as historyApi } from '../utils/api';
import { fmtUSD, fmtRMB, fmtBRL, fmtDate, STATUS_PT } from '../utils/helpers';
import { useT } from '../i18n/I18nContext';
import GarmentGrid from './GarmentGrid';
import { parseGrid, parseColors, garmentQty, gridLines } from '../utils/sizeGrid';

// Quantidade efetiva da linha: roupa pronta com grade preenchida usa a soma.
const rowQty = r => (r.is_garment && garmentQty(r) > 0) ? garmentQty(r) : (parseFloat(r.quantity) || 0);
const zeroSizes = g => Object.fromEntries(Object.keys(parseGrid(g)).map(k => [k, '']));

// Preço "vigente" de um item da origem, na mesma ordem usada pelo backend.
function sourcePrice(it) {
  const v = [it.negotiated_price, it.confirmed_price, it.target_price, it.initial_price]
    .find(x => x != null && x !== '' && Number(x) > 0);
  return v != null ? Number(v) : 0;
}

// ── Histórico de preços do mesmo artigo (informativo) ──────────────────────
// Carregado sob demanda, por item, ao abrir o "📈".
function PriceHistory({ item, t }) {
  const [state, setState] = useState({ loading: true, rows: null, error: false });

  React.useEffect(() => {
    let alive = true;
    historyApi.forItem(item.id)
      .then(d => { if (alive) setState({ loading: false, rows: d.rows || [], error: false }); })
      .catch(() => { if (alive) setState({ loading: false, rows: [], error: true }); });
    return () => { alive = false; };
  }, [item.id]);

  if (state.loading) return <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '8px 4px' }}>⏳ {t('common.loading')}</div>;
  if (state.error)   return <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '8px 4px' }}>{t('bulk.history_error')}</div>;
  if (!state.rows.length) return <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '8px 4px' }}>{t('bulk.history_empty')}</div>;

  return (
    <div style={{ padding: '6px 4px 10px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>
        📈 {t('bulk.history_title')}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--gray-100)' }}>
            <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('col.reference')}</th>
            <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('col.supplier')}</th>
            <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('col.type')}</th>
            <th style={{ padding: '4px 8px', textAlign: 'right' }}>{t('col.qty')}</th>
            <th style={{ padding: '4px 8px', textAlign: 'right' }}>{t('bulk.unit_price')}</th>
            <th style={{ padding: '4px 8px', textAlign: 'right' }}>BRL</th>
            <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('col.date')}</th>
          </tr>
        </thead>
        <tbody>
          {state.rows.map(r => (
            <tr key={r.item_id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
              <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{r.reference}</td>
              <td style={{ padding: '4px 8px' }}>{r.supplier_name}</td>
              <td style={{ padding: '4px 8px' }}>
                <span className="tag">
                  {r.order_type === 'sample' ? '🟠 Sample' : r.order_type === 'bulk' ? '🔵 Bulk' : '📝 ' + (STATUS_PT[r.status] || r.status)}
                </span>
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.quantity || '—'}</td>
              <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>
                {r.currency === 'RMB' ? fmtRMB(r.unit_price) : fmtUSD(r.unit_price)}
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--pantone)' }}>
                {r.unit_price_brl != null ? fmtBRL(r.unit_price_brl) : '—'}
              </td>
              <td style={{ padding: '4px 8px', color: 'var(--gray-500)' }}>{fmtDate(r.dated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>
        {t('bulk.history_note')}
        {state.rows[0]?.matched_by === 'name_similar' && ` · ${t('bulk.history_similar')}`}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal: transformar uma AMOSTRA ou COTAÇÃO em um PEDIDO BULK novo
// ═══════════════════════════════════════════════════════════════════════════
// • Escolhe quais produtos vão para o Bulk
// • O preço vem copiado da origem; a quantidade nasce em branco para preencher
// • Preço e quantidade são editáveis aqui
// • "Cotar" marca o item para o fornecedor informar o preço de bulk
// • Fotos e arquivos do item acompanham o produto
export default function BulkOrderModal({ quotation: q, onClose, onCreated, toast }) {
  const t = useT();
  const isRMB = q.currency === 'RMB';
  const fmt = isRMB ? fmtRMB : fmtUSD;
  const items = useMemo(() => q.items || [], [q.items]);

  const [rows, setRows] = useState(() => items.map(it => ({
    source_item_id: it.id,
    product: it.product,
    specification: it.specification,
    moq: it.moq,
    photo: (it.files || []).find(f => f.file_type === 'image') || null,
    fileCount: (it.files || []).length,
    origin_price: sourcePrice(it),
    origin_qty: it.quantity || 0,
    selected: true,
    quantity: '',                       // nasce em branco — o usuário preenche
    price: sourcePrice(it) ? String(sourcePrice(it)) : '',
    quote_bulk_price: false,
    // Roupa pronta: a grade do Bulk nasce com os tamanhos da origem, sem qtd
    is_garment: !!it.is_garment,
    origin_lines: gridLines(it),
    size_grid: zeroSizes(it.size_grid),
    // cores da origem, sem quantidade — a grade do Bulk é outra
    color_grid: parseColors(it.color_grid).map(c => ({ ...c, sizes: zeroSizes(c.sizes) })),
  })));
  const [copyFiles, setCopyFiles] = useState(true);
  const [copyQuotationFiles, setCopyQuotationFiles] = useState(true);
  const [openHistory, setOpenHistory] = useState(null);
  const [saving, setSaving] = useState(false);

  const patch = useCallback((idx, changes) => {
    setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...changes } : r)));
  }, []);

  const selected = rows.filter(r => r.selected);
  const allSelected = rows.length > 0 && selected.length === rows.length;
  const toQuoteCount = selected.filter(r => r.quote_bulk_price).length;
  const missingQty = selected.filter(r => !rowQty(r)).length;
  const totalFob = selected.reduce(
    (s, r) => s + (r.quote_bulk_price ? 0 : (parseFloat(r.price) || 0) * rowQty(r)), 0);

  async function handleCreate() {
    if (!selected.length) return toast?.(t('bulk.error_no_items'), 'error');
    setSaving(true);
    try {
      const result = await ordersApi.createBulk(q.id, {
        copy_files: copyFiles,
        copy_quotation_files: copyQuotationFiles,
        items: selected.map(r => ({
          source_item_id: r.source_item_id,
          quantity: rowQty(r),
          ...(r.is_garment ? { size_grid: r.size_grid, color_grid: r.color_grid } : {}),
          price: r.quote_bulk_price ? 0 : (r.price === '' ? null : parseFloat(r.price)),
          quote_bulk_price: r.quote_bulk_price,
        })),
      });
      toast?.(`${t('bulk.created')} ${result.reference}`, 'success');
      onCreated?.(result);
    } catch (e) {
      toast?.(e.response?.data?.error || t('common.error_saving'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const th = { padding: '7px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', fontSize: 13, verticalAlign: 'middle' };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">🔵 {t('bulk.title')}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
              {t('bulk.from')} <strong style={{ fontFamily: 'var(--font-mono)' }}>{q.reference}</strong>
              {' · '}{q.supplier_name}
              {' · '}<span className="tag">{q.order_type === 'sample' ? '🟠 Sample' : q.order_type === 'bulk' ? '🔵 Bulk' : '📝 ' + t('nav.quotations')}</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div style={{
            background: 'var(--pantone-pale)', borderRadius: 'var(--radius)', padding: '10px 14px',
            fontSize: 12, color: 'var(--gray-700)', marginBottom: 14, lineHeight: 1.5,
          }}>
            {t('bulk.intro')}
          </div>

          <div className="table-wrap" style={{ maxHeight: '46vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-100)', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ ...th, width: 34 }}>
                    <input type="checkbox" checked={allSelected} aria-label={t("bulk.select_all")}
                      onChange={e => setRows(rs => rs.map(r => ({ ...r, selected: e.target.checked })))} />
                  </th>
                  <th style={{ ...th, width: 52 }}></th>
                  <th style={th}>{t('col.product')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('bulk.origin_price')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>{t('bulk.new_qty')} *</th>
                  <th style={{ ...th, textAlign: 'right', width: 120 }}>{t('bulk.new_price')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 92 }}>{t('bulk.to_quote')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>{t('col.fob_total')}</th>
                  <th style={{ ...th, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const lineTotal = r.quote_bulk_price ? null
                    : (parseFloat(r.price) || 0) * rowQty(r);
                  const gridLocked = r.is_garment && garmentQty(r) > 0;
                  return (
                    <React.Fragment key={r.source_item_id}>
                      <tr style={{
                        borderBottom: '1px solid var(--gray-100)',
                        opacity: r.selected ? 1 : .45,
                        background: r.selected ? 'transparent' : 'var(--gray-50)',
                      }}>
                        <td style={td}>
                          <input type="checkbox" checked={r.selected} aria-label={`${t("bulk.select_item")}: ${r.product}`}
                            onChange={e => patch(idx, { selected: e.target.checked })} />
                        </td>
                        <td style={td}>
                          {r.photo
                            ? <img src={`/uploads/${r.photo.filename}`} alt=""
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)' }} />
                            : <span style={{ fontSize: 22, color: 'var(--gray-300)' }}>📦</span>}
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{r.product}</div>
                          {r.specification && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{r.specification}</div>}
                          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>
                            {r.moq ? `MOQ ${r.moq} · ` : ''}
                            {r.fileCount ? `📎 ${r.fileCount} ${t('bulk.files_follow')}` : t('bulk.no_files')}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: 'var(--gray-500)' }}>
                          {r.origin_price ? fmt(r.origin_price) : '—'}
                          {r.origin_qty ? <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{r.origin_qty} un</div> : null}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <input type="number" min="0" step="1" value={gridLocked ? garmentQty(r) : r.quantity}
                            disabled={!r.selected} readOnly={gridLocked}
                            title={gridLocked ? 'Soma da grade de tamanhos' : undefined}
                            placeholder={t('bulk.qty_placeholder')}
                            onChange={e => patch(idx, { quantity: e.target.value })}
                            style={{ width: 96, textAlign: 'right', padding: '5px 7px', fontSize: 13 }} />
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <input type="number" min="0" step="0.01" value={r.quote_bulk_price ? '' : r.price}
                            disabled={!r.selected || r.quote_bulk_price}
                            placeholder={r.quote_bulk_price ? t('bulk.to_quote_short') : '0.00'}
                            onChange={e => patch(idx, { price: e.target.value })}
                            style={{ width: 104, textAlign: 'right', padding: '5px 7px', fontSize: 13 }} />
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <label title={t('bulk.to_quote_hint')} style={{ cursor: r.selected ? 'pointer' : 'default' }}>
                            <input type="checkbox" checked={r.quote_bulk_price} disabled={!r.selected}
                              aria-label={`${t("bulk.to_quote")}: ${r.product}`}
                              onChange={e => patch(idx, { quote_bulk_price: e.target.checked })} />
                          </label>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                          {r.quote_bulk_price
                            ? <span style={{ fontSize: 11, color: 'var(--pantone)', fontWeight: 700 }}>{t('bulk.to_quote_short')}</span>
                            : (lineTotal ? fmt(lineTotal) : '—')}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button type="button" title={t('bulk.history_title')}
                            onClick={() => setOpenHistory(openHistory === r.source_item_id ? null : r.source_item_id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: 2 }}>
                            📈
                          </button>
                        </td>
                      </tr>
                      {r.is_garment && r.selected && (
                        <tr data-testid="bulk-garment-row">
                          <td colSpan={9} style={{ background: 'var(--accent-light, #f7f0f2)', padding: '6px 12px 10px 96px' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--pantone)', textTransform: 'uppercase', marginBottom: 4 }}>
                              👗 Grade do pedido
                              {r.origin_lines.length > 0 && (
                                <span style={{ fontWeight: 500, color: 'var(--gray-500)', textTransform: 'none', marginLeft: 8 }}>
                                  origem: {r.origin_lines.join(' | ')}
                                </span>
                              )}
                            </div>
                            <GarmentGrid value={{ size_grid: r.size_grid, color_grid: r.color_grid }}
                              onChange={g => patch(idx, { size_grid: g.size_grid, color_grid: g.color_grid })} />
                          </td>
                        </tr>
                      )}
                      {openHistory === r.source_item_id && (
                        <tr>
                          <td colSpan={9} style={{ background: 'var(--gray-50)', padding: '0 12px' }}>
                            <PriceHistory item={{ id: r.source_item_id }} t={t} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: 'var(--gray-600)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={copyFiles} onChange={e => setCopyFiles(e.target.checked)} />
              {t('bulk.copy_files')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={copyQuotationFiles} onChange={e => setCopyQuotationFiles(e.target.checked)} />
              {t('bulk.copy_quotation_files')}
            </label>
          </div>

          {missingQty > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#B45309', background: '#FFFBEB',
              border: '1px solid #FDE68A', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
              ⚠️ {t('bulk.warn_qty').replace('{n}', missingQty)}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>
            <strong>{selected.length}</strong> {t('bulk.items_selected')}
            {toQuoteCount > 0 && <> · <strong>{toQuoteCount}</strong> {t('bulk.to_quote_count')}</>}
            {' · '}{t('col.fob_total')}: <strong style={{ color: 'var(--pantone)' }}>{fmt(totalFob)}</strong>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>{t('btn.cancel')}</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !selected.length}>
              {saving ? '⏳ ...' : `🔵 ${t('bulk.create_btn')}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
